import { useEffect, useRef, useState } from 'react';
import * as M from 'maplibre-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Localization, SettingsManager } from '@viamap/viamap2-common';
import * as turf from '@turf/turf';
import { Legend } from 'src/components/Legend';
import { OtherLegendSpec } from 'src/common/managers/Types';
import { getMapStyle } from 'src/managers/LinkToMapStyle';
import { MapFacadeMaplibre } from 'src/managers/MapFacadeMaplibre';
import { MitPatt } from 'src/components/GenerateGeomComponents';
import { OnMapButton } from 'src/components/GlassButtons';
import { MIBIcon } from 'src/componentsUtils/MIBIcon';

export const zoomLevelShowingItemsInsteadOfGroups = 8;
export const zoomLevelClusterMax = 12;
export type BBOXTURF = number[];
export type ITEMKEY = number;

export type MapState = {
  center: [number, number],
  pitch: number,
  zoom: number,
  bearing: number,
  bbox?: BBOXTURF
}

export type SourceSpecs = { [id: string]: any };
export type LayerSpecs = { [id: string]: any };

type Props = {
  orthoPhoto: boolean,
  sources?: SourceSpecs,
  layers?: LayerSpecs,
  height?: string,
  width?: string,
  cTitle?: string,
  cLegend?: { ico: string, title: string }[],
  fullScreenControl?: boolean,
  drawControls?: boolean,
  navigationControls?: boolean,
  hideBuildings?: boolean,
  mapState?: MapState,
  beforeId?: string,
  options?: Partial<M.MapOptions>,
  showLegend?: boolean,
  otherLegends?: { [legendId: string]: OtherLegendSpec },

  onMove?: (newMapState: MapState) => void,
  // onCircle?: (center: MitLatLng, radiusMeters: number) => void,
  onPolygon?: (polygon: any) => void,
  onMapInitialization?: (mapControler: MapInstanceController) => void,
  onMapReady?: (map: MapFacadeMaplibre) => void,

  // featureLayers?: { [key: string]: FeatureLayer },
  // cadasters?: boolean,
  // dataSourceGeoJSON?: any,
  // dataSourceItemsJSON?: any,
  // dataSourceFilterJSON?: any,
  // selectBFE?: (bfeNumber: number, coord: [number, number]) => void,
  // selectedItems?: ITEMKEY[]
}

abstract class MapInstanceController {
  abstract zoomToFeatureJSON(data: any, maxZoom?: number): void;
}
export type MapInstanceControllerType = MapInstanceController;

class MapInstanceControllerMaplibre implements MapInstanceController {
  private map: M.Map;
  constructor(map: M.Map) {
    this.map = map;
  }

  public zoomToFeatureJSON(data: any, maxZoom?: number) {
    try {
      let boundingBox = turf.bbox(data.data);
      this.map.fitBounds(
        ((boundingBox.length === 4) ? boundingBox : boundingBox.splice(0, 4)) as [number, number, number, number],
        {
          // linear:true,
          padding: { top: 25, bottom: 25, left: 25, right: 25 },
          maxZoom: maxZoom || 19
        }
      );
    } catch (err) {
      console.error("Got error in fitbounds", err);
      throw new Error("Got error in fitbounds" + JSON.stringify(err));
    }
  }

}

export const EmbeddedVectorMapEnhanced = (props: Props) => {

  const [map, setMap] = useState<M.Map>();
  const [showLegend, setShowLegend] = useState<boolean>(false)

  let blockMouseEvents: boolean = false;
  function setBlockMouseEvents(value: boolean) { blockMouseEvents = value; }

  function getBbox(map: M.Map): BBOXTURF {
    let bounds: M.LngLatBounds = map.getBounds();
    let bboxMap: any = bounds.toArray();
    let bbox = [...bboxMap[0], ...bboxMap[1]];
    return bbox;
  }

  function updateArea(e: any, draw: any) {
    if (e.features.length > 0) {
      props.onPolygon?.(e);

      // Remove drawn element such that drawing the area can be controlled from caller.
      if (draw) {
        draw.deleteAll();
      } else {
        console.error("Draw not set");
      }
    }
  }

  function deleteArea() {
    props.onPolygon?.(undefined);
  }

  const mapContainer = useRef(null);

  // MAP INITIALIZATION
  useEffect(() => {
    let options = {};
    let vectorStylePath = getMapStyle(SettingsManager.getSystemSetting("vectorMapStyle", undefined))
    if (vectorStylePath) {
      // ToDo: Get from AWS S3 library
      options = {
        style: vectorStylePath,
        sources: {},
        hash: false,
        zoom: (props.mapState?.zoom || 7),
        pitch: (props.mapState?.pitch || 0),
        bearing: (props.mapState?.bearing || 0),
        center: (props.mapState?.center || [11.428477, 55.375788]),
        ...props.options
      };
      // Note: Uses viamap default style if not set.
    }
    let cleaning: M.Map
    let Mitmap = new MapFacadeMaplibre(mapContainer.current, false, options)
    let map = Mitmap.getMapPop() as M.Map;
    map.once('load', () => {
      props.onMapReady?.(Mitmap);
    });
    setMap(map);
    cleaning = map;

    // ----------------------------------- load icons ----------------------------------
    let scale = 1;
    map.loadImage(`https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png`).then((image) => {
      map.addImage('blue-pin', image.data as any, { pixelRatio: scale }); // 38x55px, shadow adds 5px (for scale eq 1)
    })

    map.loadImage(`https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png`).then((image) => {
      map.addImage('red-pin', image.data as any, { pixelRatio: scale }); // 38x55px, shadow adds 5px (for scale eq 1)
    })


    if (props.navigationControls) {
      map.addControl(new M.NavigationControl({ showZoom: true }), 'top-left');
    }

    if (props.fullScreenControl) {
      map.addControl(new M.FullscreenControl({}));
    }

    if (props.drawControls) {

      var draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: false,
        },
        clickBuffer: 20,
        // defaultMode: "draw_circle",
        // userProperties: true,
        // modes: {
        //   ...MapboxDraw.modes,
        //   draw_circle  : CircleMode,
        //   drag_circle  : DragCircleMode,
        //   direct_select: DirectMode,
        //   simple_select: SimpleSelectMode
        // }
      });
      map.addControl(draw as any);

      map.on('draw.create', (e) => updateArea(e, draw));
      map.on('draw.delete', deleteArea);
      map.on('draw.update', (e) => updateArea(e, draw));

    }

    function postMapStateToParent() {
      if (props.onMove && !blockMouseEvents) {
        let bbox = getBbox(map);
        // console.info("Moveevent to be sent");
        props.onMove({
          bearing: map.getBearing(),
          pitch: map.getPitch(),
          zoom: map.getZoom(),
          center: (map.getCenter().toArray() as [number, number]),
          bbox: bbox
        })
      }
    }
    if (props.onMove && map) {
      map.on('moveend', () => {
        postMapStateToParent();
      })
      map.on('zoomend', () => {
        postMapStateToParent();
      })
      map.on('load', () => {
        postMapStateToParent();
      })
      setBlockMouseEvents(false);
    }

    props.onMapInitialization?.(new MapInstanceControllerMaplibre(map));


    return () => cleaning?.remove()
  }, []); // Run only once

  useEffect(() => {
    if (map && props.mapState && !blockMouseEvents) {
      setBlockMouseEvents(true);

      map.setZoom(props.mapState.zoom || 7);
      map.setPitch(props.mapState.pitch || 0);
      map.setBearing(props.mapState.bearing || 0);
      map.setCenter(props.mapState.center || [
        11.428477,
        55.375788
      ]);
      setBlockMouseEvents(false);
      map.resize();
    }
  }, [props.mapState]);

  useEffect(() => {
    if (map?.style) map.setLayoutProperty?.('orthophoto', 'visibility', props.orthoPhoto ? 'visible' : 'none');
  }, [props.orthoPhoto, map]); // Run when map becomes available or showOrtoPhoto is changed

  useEffect(() => {
    if (map?.style) map.setLayoutProperty?.('building-3d', 'visibility', props.hideBuildings ? 'none' : 'visible');
  }, [props.hideBuildings, map]); // Run when map becomes available or showBuildings is changed

  useEffect(() => {
    if (map && props.sources && props.layers && Object.keys(props.sources).length > 0) {
      Object.keys(props.layers).forEach((layerId) => {
        let lr = props.layers && props.layers[layerId];
        if (!map.getLayer(lr.id)) {
          if (!map.getSource(lr.source)) {
            let data = props.sources && props.sources[lr.source];
            map.addSource(lr.source, data);
          }
          map.addLayer(lr, props.beforeId);

        } else {
          let src = props.sources && props.sources[lr.source];
          if (src && src.type === "geojson") {
            (map as any)?.getSource(lr.source)?.setData(src.data);
          }
        }

      })
    }
  }, [props.layers, map]); // Run when map becomes available or showOrtoPhoto is changed

  // Map render function
  return (
    <>
      {(props.otherLegends || props?.cLegend) ?
        showLegend ? 
        <Legend
          showWindow={1}
          layers={[]}
          callBackOnStyleChange={() => 0}
          callBackToHideWindow={() => { setShowLegend(false) }}
          otherLegends={{ ...props.otherLegends, "custom": cLegend({ title: props.cTitle || "Legend", legend: props.cLegend }) }}
          />
          :
          <OnMapButton
            title-placement='right'
            className="ReOpenLegend" title={Localization.getText("Show Legend")} onClick={() => setShowLegend(true)} style={{ position: "absolute", bottom: "42px", left: "10px", zIndex: 2 }}>
            <MIBIcon icon="legend" />
          </OnMapButton>
        : <></>}
      <div ref={mapContainer} style={{ height: props.height || "100%", width: props.width || "" }} className="map-container" />
    </>
  );
}

function cLegend(props: { title: string, legend?: { ico: string, title: string }[] }): { showLegend: () => boolean, renderLegend: () => JSX.Element } {
  if (!props.legend?.length) {
    return {
      showLegend: () => { return false },
      renderLegend: () => { return <></> }
    }
  }
  return {
    showLegend: () => { return true },
    renderLegend: () => {
      return (
        <div className="Group" key={props.title}>
          <div className="Line">
            <div className="TitleIcon"><MIBIcon icon="layer" /></div>
            <div className='Title' >
              {props.title}
            </div>
          </div>
          <div className="Group" >
            {props.legend?.map((a, idx) => {
              let label = a.title;
              return (
                <div key={idx} className="LegendImg"  >
                  {a.ico ? <MitPatt opacity={1} pattern={a.ico} /> : null}
                  <div className="LegendImgText">
                    {label}
                  </div>
                </div>
              )
            }) ?? <></>}
          </div>
        </div>
      )
    }
  }
}